import Avatar from '@mui/material/Avatar';
import MuiListItem from '@mui/material/ListItem';
import type { ListItemProps } from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import classNames from 'clsx';
import React from 'react';

import { button, root, selected, text } from './ListItem.scss';

interface Props extends Omit<ListItemProps<React.ElementType>, 'classes'> {
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  avatar?: React.ReactNode;
  textOnly?: boolean;
  avatarURL?: string;
  classes?: {
    root?: string;
    button?: string;
    selected?: string;
    itemText?: string;
    avatarContainer?: string;
    avatar?: string;
    primaryText?: string;
    secondaryText?: string;
  };
  disableRipple?: boolean;
}

const ListItem: React.FC<Props> = ({
  onClick,
  avatarURL,
  classes = {},
  selected: itemSelected,
  primary,
  secondary,
  avatar,
  textOnly,
  children,
  isButton = true,
  alignItems,
  style,
  disableRipple = false
}) => {
  const {
    avatarContainer,
    avatar: avatarClass,
    primaryText,
    secondaryText,
    itemText,
    root: rootClass,
    button: buttonClass,
    selected: selectedClass
  } = classes;

  const content = (
    <>
      {!textOnly && (
        <ListItemAvatar className={avatarContainer}>
          {avatarURL ? (
            <Avatar className={avatarClass} src={avatarURL} />
          ) : (
            <Avatar className={avatarClass}>{avatar}</Avatar>
          )}
        </ListItemAvatar>
      )}
      <ListItemText
        primary={primary}
        secondary={secondary}
        classes={{
          root: classNames(text, itemText),
          primary: primaryText,
          secondary: secondaryText
        }}
      />
      {children}
    </>
  );

  return isButton ? (
    <ListItemButton
      className={classNames(root, rootClass, {
        [button]: !!onClick,
        [buttonClass || '']: !!onClick && buttonClass,
        [selected]: itemSelected,
        [selectedClass || '']: itemSelected && selectedClass
      })}
      onClick={onClick}
      selected={itemSelected}
      alignItems={alignItems}
      style={style}
      disableRipple={disableRipple}
    >
      {content}
    </ListItemButton>
  ) : (
    <MuiListItem
      onClick={onClick}
      alignItems={alignItems}
      style={style}
      className={classNames(root, rootClass, {
        [button]: !!onClick,
        [buttonClass || '']: !!onClick && buttonClass,
        [selected]: itemSelected,
        [selectedClass || '']: itemSelected && selectedClass
      })}
    >
      {content}
    </MuiListItem>
  );
};

export default ListItem;
